import { createMachine, assign, send, spawn } from 'xstate';

import airbrakeMachine from './airbrake-machine';
import analyticsMachine from './analytics-machine';
import authMachine from './auth-machine';
import firestoreMachine from './firestore-machine';
import firebaseMachine from './firebase-machine';
import loggerMachine from './logger-machine';

import { createFulcrumMachine } from './fulcrum-machine';
import { createLoaderMachine } from './loader-machine';
import { createLeadMachine } from './lead-machine';
import { createOutboundCheckoutMachine } from './outbound-checkout-machine';

const PRIORITIES = {
  VERY_HIGH: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  VERY_LOW: 4,
};

const PRIORITIES_BY_SERVICE = {
  firebase: PRIORITIES.VERY_HIGH,
  auth: PRIORITIES.HIGH,
  analytics: PRIORITIES.HIGH,
  firestore: PRIORITIES.VERY_LOW,
  airbrake: PRIORITIES.VERY_LOW,
  'fulcrum/page': PRIORITIES.VERY_LOW,
  'fulcrum/click': PRIORITIES.VERY_LOW,
  'fulcrum/media': PRIORITIES.VERY_LOW,
  'funnel/leads': PRIORITIES.VERY_LOW,
  'checkout/outbound': PRIORITIES.VERY_LOW,
};

// See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
const sortHighToLow = (a, b) => {
  if (a.priority < b.priority) {
    return -1;
  }

  if (a.priority > b.priority) {
    return 1;
  }

  return 0;
};

const recurseInitializeDeps = (
  allCurrentInstalls,
  newInstallsThisTick,
  currentMod,
) => {
  // If this module has dependencies, we need to initialize them BEFORE we
  // initialize the current one with them as dependencies
  if (
    Array.isArray(currentMod.dependencies) &&
    currentMod.dependencies.length > 0
  ) {
    for (const dep of currentMod.dependencies) {
      if (
        allCurrentInstalls[dep] &&
        !newInstallsThisTick[dep] &&
        typeof dep.instance === 'undefined'
      ) {
        recurseInitializeDeps(
          allCurrentInstalls,
          newInstallsThisTick,
          allCurrentInstalls[dep],
        );
      }
    }
  }

  if (
    currentMod &&
    !currentMod.instance &&
    (currentMod.dependencies || []).every((dep) => dep.instance !== 'undefined')
  ) {
    const currentDeps = currentMod.dependencies
      .map((serviceId) => {
        // Make sure we're returning the dep we just initialized,
        // or the one we initialized on a previous tick.
        if (newInstallsThisTick[serviceId]) {
          return newInstallsThisTick[serviceId];
        }
        return allCurrentInstalls[serviceId];
      })
      .reduce((acc, v) => ({ ...acc, [v.serviceId]: v }), {});

    // Initialize the current dependency.
    newInstallsThisTick[currentMod.serviceId] = {
      ...currentMod,
      instance: currentMod.bundle.init
        ? currentMod.bundle.init({ dependencies: currentDeps })
        : null,
    };

    return newInstallsThisTick;
  }

  return newInstallsThisTick;
};

const initializeDependencies = (installs, rootDep) => {
  return recurseInitializeDeps(installs, {}, rootDep);
};

const recurseCheckAllModuleDependenciesInitialized = (
  allCurrentInstalls,
  currentMod,
) => {
  if (!currentMod) {
    return false;
  }

  if (
    !currentMod.dependencies ||
    (Array.isArray(currentMod.dependencies) &&
      currentMod.dependencies.length === 0)
  ) {
    return true;
  }

  return currentMod.dependencies.every((serviceId) =>
    recurseCheckAllModuleDependenciesInitialized(
      allCurrentInstalls,
      allCurrentInstalls[serviceId],
    ),
  );
};

const checkAllModuleDependenciesInitialized = (allCurrentInstalls, rootMod) => {
  return recurseCheckAllModuleDependenciesInitialized(
    allCurrentInstalls,
    rootMod,
  );
};

// A function that returns a new install object
const createInstall = (serviceId) => ({
  serviceId,
  dependencies: [],
  bundle: null,
  instance: null,
});

// These services are injected into the supervisor's context immediately instead of
// using lazy loading like some of the heavier services do.
const defaultInstallations = () => ({
  'fulcrum/page': createInstall('fulcrum/page'),
  'fulcrum/click': createInstall('fulcrum/click'),
  'fulcrum/media': createInstall('fulcrum/media'),
  'funnel/leads': createInstall('funnel/leads'),
  'checkout/outbound': createInstall('checkout/outbound'),
});

const supervisorMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5SwK4AcwCcBuBLWA9pgHS4QA2YAxADIDyAggCICiASgPpssDKdNANRZMA2gAYAuolBoCsXABdcBAHbSQAD0QBaAIy6AHAE5iYgOwA2Cwd0AWfboDMRiwBoQATx0BWI7dPOFkaGFt7eBo4ATAC+0e6oGDj4RKQU1DzsAgCSAMIsxDkMNDTiUkggsvJKqupaCHpiYrrEtha2Ee0GkQZi3m6eOrZGZqbBZraOdmaNrd6x8ehYeIQkAGbkKLAAFgCKKGD7sLSMrJzcfILCpeqVispq5XXaFpEmZl0GvgYWk5FmRu4vPUgmJiC4Xq8jAZbP9ofMQAklsk1httnsDnAqBk2Nk8gUiiVJDc5Hcao8dH9vMRIr5nEZwo5Wj9vICdNN-PTbN59M5vGZdL14YikitiOtNrt9ocqNdyrdqg9QE9vJNTOZvGJgp8ueFWfVHNMWuM-p8IRYzHM4gjFiKUuLtrgVFAaAQAIYQLDo-bHZjsLi8fhCUREuUkhW1HSGSJgtoa029IyvFkDepciwtSKOMTdZwdXQWIU25Z21FbR3Ot0ezBe9KZXL5QrFWUyMP3CMIfnERyOCxiaxGMQRH7-PXaeyOFq2GkWAUhCIGQuJYsoiXll3uz1S6jNiqtslKnyqxoWzWGbw6gyj3Q06lGO+D2y9fkDsyLpGi+1lp1ZFR3V3kXAAC8sFgGsfVOf0LiDHd5Tbcl6j6LsszMSJ+3pGxs1HTN03nGxwisFxugXK1hWXMVS3LH8-wA4DMFArcsTrPFG0JMoWyqOCDwQJwJxCPwzCiOxu0+UcJ0cFVE01RpdHCKcIjfW0Vwdb9fyUf8gJAsCYL3RVNEPZpjzCIwDRhGwzD1SxiF0O8+X+XtHAMfkFLIz9yxYbAwBUBQwPoX0zgDS5gzY3cOP3PSEMs81Om+R9viaPVxIMKy2jsXsaXPSZnORcjVyddzPO8hjsVxBsCW00LdKeSImlGO9oT6XNpkiBLuzBdlXhpSJWjEVoso-Ci8o8rytJDdjSUqnQDRMcTvC6-RQifH49XCKksyGNpp2sYw+pLXKoHy4aGJEXRgtgsKngFO8Wiibo+g6H5HD1SJdBGc0BJhQdqrqowdqUr8oDYOACHIDzqwY3yIPOQMrlGkLxvbPRGWjbtjKsPsYQ1R6UwaExrIMGwunGf5ulfEii2y1ynUBwgQc3DFGJxet8SbWGzom+pjGaF4XGGByXr7fogXGakBSGOxzCiDLfpy5SAaB2mwfp8r4fg7RYSsvxfGMwwJj5UTe2Id4mtabsGTERxpcpuWadBrSTuJCqEZcZoLSzJwJneXtzOxzN-CGecew1bkZItsml2yiB8DQV0FAAY3+g6FCOCG-ShwLlfDeDnH8Jxbs1Loe1jZakphdpGXMfGYWM2xpcj2Bo7jhOhqThmSuZ1iHZVrjHxMPkYWvYY+x1wXEANUEeksAVHLdnoCzD98Ujrhv47c5ujgzzjwu0aEwV8KwZzsb56r1PxiHx75qsHXps3zX6qHOFgABUN-OyNxPTc9jBVA-xnaUdZvTAOT6xlGjfETHfAACtwAAYk-HIAAJF+7NtBAOIF7bkckBzcm9kCZ45orJciARqekbQfrwhUAQD08ByikWymQSgndM5cQaKEMEmZxKCUZMMAE2NGQjGsi9KEfxUpRDngscO-UJQ1moWNJhW8AHEGIVEFw0JUKTFHP7UwMkaSGGhJXGu89FIy3+uuKsNZGGb2VJmNBx4ky+BQjOLCNg2qawmN1PwZDxELz+pRVSuB1K0XohiCxr99SPjQZdYIL5ug0lsP-EYrxLBAKwc9fGlsBr7WbuY0MjtVaMgnGEAUs0vooV0E9Wat5zz0metZcwdh0l7WpsDW2W4QnIKhP4fG-I+i+C+nE7GERDb8lieEVC55fC1yjjHFeg0CoyLhnIuo15-AqnzAXJo4QZLJiBM9fw-xzyWD7hjRkv02kI3Fis9o9Iez6F-pebGwxAHjLShMGk9JYixCAA */
  createMachine(
    {
      context: {
        priority: PRIORITIES.VERY_HIGH,
        loaders: [],
        waiting: [],
        resolved: [],
        initializers: [],
        events: [],
        installations: defaultInstallations(),
      },
      preserveActionOrder: true,
      predictableActionArguments: true,
      invoke: [
        {
          src: loggerMachine,
          id: 'logger',
        },
        {
          src: firebaseMachine,
          id: 'firebase',
        },
        {
          src: authMachine,
          id: 'auth',
        },
        {
          src: analyticsMachine,
          id: 'analytics',
        },
        {
          src: firestoreMachine,
          id: 'firestore',
        },
        {
          src: airbrakeMachine,
          id: 'airbrake',
        },
        {
          src: createFulcrumMachine('fulcrum/page'),
          id: 'fulcrum/page',
        },
        {
          src: createFulcrumMachine('fulcrum/click'),
          id: 'fulcrum/click',
        },
        {
          src: createFulcrumMachine('fulcrum/media'),
          id: 'fulcrum/media',
        },
        {
          src: createLeadMachine('funnel/leads'),
          id: 'funnel/leads',
        },
        {
          src: createOutboundCheckoutMachine('checkout/outbound'),
          id: 'checkout/outbound',
        },
      ],
      id: 'supervisor',
      on: {
        RESET: {
          target: '.idle',
          actions: 'clearState',
        },
        PREFETCH: {
          target: '.flushQueues',
          actions: 'assignWorkToLoaderQueue',
        },
        'SERVICE.CALL': {
          actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
        },
      },
      initial: 'flushQueues',
      states: {
        idle: {
          on: {
            LOADER_RESOLVED: {
              target: 'flushQueues',
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              target: 'flushQueues',
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
        flushQueues: {
          entry: 'assignHighestPriorityToContext',
          always: {
            target: 'flushingLoaderQueue',
          },
          on: {
            LOADER_RESOLVED: {
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
        flushingLoaderQueue: {
          entry: 'spawnHighestPriorityLoadersToWaitQueue',
          exit: 'clearSpawnedLoadersFromLoaderQueue',
          always: {
            target: 'flushingInitializersQueue',
          },
          on: {
            LOADER_RESOLVED: {
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
        flushingInitializersQueue: {
          entry: 'initializeModulesWithDependencies',
          exit: 'clearInitializedModulesFromInitializerQueue',
          always: {
            target: 'flushingEventQueue',
          },
          on: {
            LOADER_RESOLVED: {
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
        flushingEventQueue: {
          always: [
            {
              target: 'dispatchingEvents',
              cond: 'shouldDispatchEventNextTick',
            },
            {
              target: 'flushingResolverQueue',
            },
          ],
          on: {
            LOADER_RESOLVED: {
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
        flushingResolverQueue: {
          entry: [
            'assignResolvedLoaderToInitializerQueue',
            'assignMissingLoadersToLoaderQueue',
          ],
          always: [
            {
              target: 'flushQueues',
              cond: 'thereIsPendingWorkToDo',
            },
            {
              target: 'idle',
            },
          ],
          on: {
            LOADER_RESOLVED: {
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
        dispatchingEvents: {
          entry: 'dispatchEvent',
          exit: 'removeEventFromEventQueue',
          always: {
            target: 'flushingEventQueue',
          },
          on: {
            LOADER_RESOLVED: {
              actions: 'assignWorkToResolverQueue',
            },
            'SERVICE.CALL': {
              actions: ['assignWorkToEventQueue', 'assignWorkToLoaderQueue'],
            },
          },
        },
      },
    },
    {
      guards: {
        thereIsPendingWorkToDo: (context) => {
          if (context.loaders.length > 0) {
            return true;
          }

          if (
            context.initializers.some((mod) => {
              return checkAllModuleDependenciesInitialized(
                context.installations,
                mod,
              );
            })
          ) {
            return true;
          }

          return false;
        },
        shouldDispatchEventNextTick: (context) => {
          if (context.events.length === 0) {
            return false;
          }

          const nextTickEventPeek = context.events[0];

          if (!nextTickEventPeek) {
            return false;
          }

          const nextTickModule =
            context.installations[nextTickEventPeek.serviceId];

          if (!nextTickModule) {
            return false;
          }

          // This check handles undefined modules
          return checkAllModuleDependenciesInitialized(
            context.installations,
            nextTickModule,
          );
        },
      },

      services: {},

      actions: {
        clearState: assign({
          loaders: [],
          waiting: [],
          resolved: [],
          initializers: [],
          installations: defaultInstallations(),
          events: [],
          priority: PRIORITIES.VERY_HIGH,
        }),
        dispatchEvent: send(
          (context) => {
            const eventToDispatchThisTick = context.events[0];
            return {
              type: eventToDispatchThisTick.event,
              payload: {
                fromId: eventToDispatchThisTick.fromId,
                arguments: eventToDispatchThisTick.arguments,
                bundle:
                  context.installations[eventToDispatchThisTick.serviceId]
                    .bundle,
                instance:
                  context.installations[eventToDispatchThisTick.serviceId]
                    .instance,
              },
            };
          },
          {
            to: (context) => {
              return context.events[0].serviceId;
            },
          },
        ),
        removeEventFromEventQueue: assign({
          events: (context) => {
            return context.events.slice(1);
          },
        }),
        assignResolvedLoaderToInitializerQueue: assign({
          initializers: (context) => {
            if (context.resolved.length === 0) {
              return context.initializers;
            }

            return context.initializers.concat(context.resolved);
          },
          resolved: [],
        }),
        assignMissingLoadersToLoaderQueue: assign({
          loaders: (context) => {
            const missing = [];

            context.initializers.forEach((mod) => {
              if (
                !checkAllModuleDependenciesInitialized(
                  context.installations,
                  mod,
                )
              ) {
                mod.dependencies.forEach((serviceId) => {
                  if (
                    !context.installations[serviceId] &&
                    !['loaders', 'waiting', 'resolved', 'initializers'].some(
                      (queue) =>
                        Boolean(
                          context[queue].find((m) => m.serviceId === serviceId),
                        ),
                    )
                  ) {
                    missing.push({
                      serviceId: serviceId,
                      priority: PRIORITIES_BY_SERVICE[serviceId],
                    });
                  }
                });
              }
            });

            return context.loaders.concat(missing);
          },
        }),
        assignHighestPriorityToContext: assign({
          priority: (context) => {
            if (context.loaders.length === 0) {
              return PRIORITIES.VERY_HIGH;
            }

            const nextHighestPeek = context.loaders[0].priority;

            if (!Object.values(PRIORITIES).includes(nextHighestPeek)) {
              throw new Error(`Invalid loader priority`);
            }

            return nextHighestPeek;
          },
        }),
        spawnHighestPriorityLoadersToWaitQueue: assign({
          waiting: (context) => {
            return context.loaders
              .filter((loader) => {
                if (
                  loader.priority === context.priority &&
                  !context.installations[loader.serviceId] &&
                  !['waiting', 'resolved', 'initializers'].some((queue) =>
                    Boolean(
                      context[queue].find(
                        (mod) => mod.serviceId === loader.serviceId,
                      ),
                    ),
                  )
                ) {
                  return true;
                }

                return false;
              })
              .map((mod) => {
                return {
                  ...mod,
                  ref: spawn(createLoaderMachine(mod.serviceId)),
                };
              });
          },
        }),
        clearSpawnedLoadersFromLoaderQueue: assign({
          loaders: (context) => {
            const spawnedLoaderIds = context.waiting.map(
              (spawned) => spawned.serviceId,
            );
            return context.loaders.filter(
              (loader) => !spawnedLoaderIds.includes(loader.serviceId),
            );
          },
        }),
        initializeModulesWithDependencies: assign({
          installations: (context) => {
            let newInstallsThisTick = {};

            context.initializers
              .filter(
                (modToInit) => !context.installations[modToInit.serviceId],
              )
              .forEach((modToInit) => {
                if (
                  checkAllModuleDependenciesInitialized(
                    context.installations,
                    modToInit,
                  )
                ) {
                  const installs = initializeDependencies(
                    context.installations,
                    modToInit,
                  );
                  Object.keys(installs).forEach((serviceId) => {
                    newInstallsThisTick[serviceId] = installs[serviceId];
                  });
                }
              });

            if (Object.keys(newInstallsThisTick).length > 0) {
              return {
                ...context.installations,
                ...newInstallsThisTick,
              };
            }

            return context.installations;
          },
        }),
        clearInitializedModulesFromInitializerQueue: assign({
          initializers: (context) => {
            return context.initializers.filter(
              (mod) => !Boolean(context.installations[mod.serviceId]),
            );
          },
        }),
        assignWorkToEventQueue: assign({
          events: (context, event) => {
            if (event.serviceId) {
              return context.events.concat(event);
            }
            return context.events;
          },
        }),
        assignWorkToLoaderQueue: assign({
          loaders: (context, event) => {
            const serviceIds = [];

            if (event.modules) {
              event.modules.forEach((m) => {
                if (m.serviceId) {
                  serviceIds.push(m.serviceId);
                }
              });
            } else if (event.serviceId) {
              serviceIds.push(event.serviceId);
            }

            const modulesToFetch = serviceIds
              .filter((serviceId) => {
                if (context.installations[serviceId]) {
                  return false;
                }

                if (
                  ['loaders', 'waiting', 'resolved', 'initializers'].some(
                    (queue) =>
                      Boolean(
                        context[queue].find((m) => m.serviceId === serviceId),
                      ),
                  )
                ) {
                  return false;
                }

                return true;
              })
              .map((serviceId) => {
                const priority = PRIORITIES_BY_SERVICE[serviceId];

                if (priority === undefined) {
                  throw new Error(
                    `Invalid loading priority for "${event.serviceId}"`,
                  );
                }

                return {
                  serviceId,
                  priority,
                };
              });

            return context.loaders.concat(modulesToFetch).sort(sortHighToLow);
          },
        }),
        assignWorkToResolverQueue: assign({
          resolved: (context, event) => {
            const resolvedLoader = {
              type: event.type,
              serviceId: event.data.serviceId,
              dependencies: event.data.module.default.dependencies || [],
              bundle: event.data.module.default.bundle,
              instance: undefined,
              ref: undefined,
            };

            return context.resolved.concat(resolvedLoader);
          },
          waiting: (context, event) => {
            return context.waiting.filter(
              (mod) => mod.serviceId !== event.data.serviceId,
            );
          },
        }),
      },
    },
  );

export default supervisorMachine;
